/**
 * Premium Components JavaScript
 * Handles interactivity for the redesigned product and collection pages
 */

(function() {
  'use strict';

  /* ========================================
     PREMIUM PRODUCT GALLERY
     ======================================== */

  class PremiumGallery {
    constructor(container) {
      this.container = container;
      this.mainImage = container.querySelector('[data-main-image]');
      this.thumbs = container.querySelectorAll('[data-thumb-index]');
      this.zoomBtn = container.querySelector('[data-gallery-zoom]');
      this.lightbox = document.querySelector('[data-gallery-lightbox]');
      this.currentIndex = 0;
      this.images = [];

      this.init();
    }

    init() {
      // Helper function to get high-res image URL from Shopify CDN
      const getHighResUrl = (url, size) => {
        if (!url) return url;
        // Handle Shopify CDN format: ?width=XXX or &width=XXX
        if (url.includes('width=')) {
          return url.replace(/width=\d+/, `width=${size}`);
        }
        // Handle older format: _XXXx or _XXXxYYY
        if (url.match(/_\d+x\d*\./)) {
          return url.replace(/_\d+x\d*\./, `_${size}x.`);
        }
        // If no size parameter found, try to add width parameter
        if (url.includes('?')) {
          return url + `&width=${size}`;
        }
        return url + `?width=${size}`;
      };

      // Get the main image to preserve its initial high-res URL
      const mainImg = this.mainImage ? this.mainImage.querySelector('img') : null;
      const mainImgSrc = mainImg ? mainImg.src : null;

      // Collect all images from thumbnails
      this.thumbs.forEach((thumb, index) => {
        const img = thumb.querySelector('img');
        if (img) {
          // For the first image (index 0), use the main image URL if available
          // since it's already loaded at high resolution
          const baseSrc = (index === 0 && mainImgSrc) ? mainImgSrc : img.src;
          this.images.push({
            src: getHighResUrl(baseSrc, 1200),
            largeSrc: getHighResUrl(baseSrc, 1800),
            alt: img.alt || (mainImg ? mainImg.alt : '')
          });
        }

        // Thumbnail click
        thumb.addEventListener('click', () => this.switchImage(index));
        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.switchImage(index);
          }
        });
      });

      // Zoom button
      if (this.zoomBtn) {
        this.zoomBtn.addEventListener('click', () => this.openLightbox());
      }

      // Lightbox controls
      if (this.lightbox) {
        this.lightbox.querySelectorAll('[data-lightbox-close]').forEach(btn => {
          btn.addEventListener('click', () => this.closeLightbox());
        });

        const prevBtn = this.lightbox.querySelector('[data-lightbox-prev]');
        const nextBtn = this.lightbox.querySelector('[data-lightbox-next]');

        if (prevBtn) prevBtn.addEventListener('click', () => this.prevImage());
        if (nextBtn) nextBtn.addEventListener('click', () => this.nextImage());

        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
          if (!this.lightbox.classList.contains('is-open')) return;

          if (e.key === 'Escape') this.closeLightbox();
          if (e.key === 'ArrowLeft') this.prevImage();
          if (e.key === 'ArrowRight') this.nextImage();
        });
      }
    }

    switchImage(index) {
      this.currentIndex = index;

      // Update thumbnails
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
      });

      // Update main image
      const mainImg = this.mainImage.querySelector('img');
      if (mainImg && this.images[index]) {
        mainImg.src = this.images[index].src;
        mainImg.alt = this.images[index].alt;
      }
    }

    openLightbox() {
      if (!this.lightbox) return;

      this.updateLightboxImage();
      this.lightbox.classList.add('is-open');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('lightbox-open');

      // Move lightbox to body to ensure it's above all other elements
      if (this.lightbox.parentElement !== document.body) {
        document.body.appendChild(this.lightbox);
      }
    }

    closeLightbox() {
      if (!this.lightbox) return;

      this.lightbox.classList.remove('is-open');
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.body.classList.remove('lightbox-open');
    }

    updateLightboxImage() {
      const lightboxImg = this.lightbox.querySelector('[data-lightbox-image] img');
      const counter = this.lightbox.querySelector('[data-lightbox-current]');

      if (lightboxImg && this.images[this.currentIndex]) {
        lightboxImg.src = this.images[this.currentIndex].largeSrc;
        lightboxImg.alt = this.images[this.currentIndex].alt;
      }

      if (counter) {
        counter.textContent = this.currentIndex + 1;
      }
    }

    prevImage() {
      this.currentIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.images.length - 1;
      this.switchImage(this.currentIndex);
      this.updateLightboxImage();
    }

    nextImage() {
      this.currentIndex = this.currentIndex < this.images.length - 1 ? this.currentIndex + 1 : 0;
      this.switchImage(this.currentIndex);
      this.updateLightboxImage();
    }
  }


  /* ========================================
     STICKY ADD TO CART
     ======================================== */

  class StickyAddToCart {
    constructor() {
      this.stickyBar = document.querySelector('[data-sticky-atc]');
      this.addToCartBtn = document.querySelector('[data-add-to-cart]');

      if (!this.stickyBar) return;

      this.variantSelect = this.stickyBar.querySelector('[data-sticky-variant]');
      this.stickyBtn = this.stickyBar.querySelector('[data-sticky-add-to-cart]');
      this.priceEl = this.stickyBar.querySelector('[data-sticky-price]');

      this.init();
    }

    init() {
      // Intersection Observer for visibility
      if (this.addToCartBtn) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                this.hide();
              } else {
                this.show();
              }
            });
          },
          { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
        );

        observer.observe(this.addToCartBtn);
      } else {
        // If no main add-to-cart, show after scroll
        window.addEventListener('scroll', () => {
          if (window.scrollY > 500) {
            this.show();
          } else {
            this.hide();
          }
        });
      }

      // Variant selection
      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', () => this.updateVariant());
      }

      // Add to cart
      if (this.stickyBtn) {
        this.stickyBtn.addEventListener('click', () => this.addToCart());
      }
    }

    show() {
      this.stickyBar.classList.add('is-visible');
      this.stickyBar.setAttribute('aria-hidden', 'false');
    }

    hide() {
      this.stickyBar.classList.remove('is-visible');
      this.stickyBar.setAttribute('aria-hidden', 'true');
    }

    updateVariant() {
      const selectedOption = this.variantSelect.options[this.variantSelect.selectedIndex];
      const variantId = selectedOption.value;
      const price = selectedOption.dataset.price;
      const available = selectedOption.dataset.available === 'true';

      // Update button variant ID
      this.stickyBtn.dataset.variantId = variantId;

      // Update price
      if (this.priceEl && price) {
        const priceCurrentEl = this.priceEl.querySelector('.sticky-atc__price-current');
        if (priceCurrentEl) priceCurrentEl.textContent = price;
      }

      // Update button state
      this.stickyBtn.disabled = !available;
      const btnText = this.stickyBtn.querySelector('span');
      if (btnText) {
        btnText.textContent = available ? 'Add to Cart' : 'Sold Out';
      }
    }

    addToCart() {
      const variantId = this.stickyBtn.dataset.variantId;
      if (!variantId) return;

      this.stickyBtn.classList.add('is-loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: parseInt(document.querySelector('.unikelove-product__qty-input')?.value) || 1
        })
      })
      .then(response => response.json())
      .then(data => {
        this.stickyBtn.classList.remove('is-loading');
        this.stickyBtn.classList.add('is-success');

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

        // Reset button after delay
        setTimeout(() => {
          this.stickyBtn.classList.remove('is-success');
        }, 2000);
      })
      .catch(error => {
        console.error('Error:', error);
        this.stickyBtn.classList.remove('is-loading');
      });
    }
  }


  /* ========================================
     VARIANT GUIDE MODAL
     ======================================== */

  class VariantGuide {
    constructor(container) {
      this.container = container;
      this.trigger = container.querySelector('[data-guide-trigger]');
      this.modal = container.querySelector('[data-guide-modal]');
      this.closeButtons = container.querySelectorAll('[data-guide-close]');

      if (!this.trigger || !this.modal) return;

      this.init();
    }

    init() {
      this.trigger.addEventListener('click', () => this.open());

      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
          this.close();
        }
      });
    }

    open() {
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }


  /* ========================================
     PREMIUM COLLECTION FILTERS
     ======================================== */

  class PremiumFilters {
    constructor() {
      this.handleResize = () => this.updatePriceRanges();
      this.refreshElements();

      if (!this.sidebar) return;

      this.bindEvents();
      this.initPriceRanges();
      this.bindResize();
    }

    refreshElements() {
      this.sidebar = document.querySelector('[data-collection-sidebar]');
      this.filterForm = document.querySelector('[data-filter-form]');
      this.mobileToggle = document.querySelector('[data-filter-mobile-toggle]');
      this.closeBtn = document.querySelector('[data-sidebar-close]');
      this.overlay = document.querySelector('[data-filter-overlay]');
      this.filterToggles = document.querySelectorAll('[data-filter-toggle]');
      this.priceRanges = [];
    }

    bindResize() {
      if (this.resizeBound) return;
      window.addEventListener('resize', this.handleResize);
      this.resizeBound = true;
    }

    bindEvents() {
      if (this.sidebar && this.sidebar.dataset.premiumFiltersBound) return;
      if (this.sidebar) this.sidebar.dataset.premiumFiltersBound = 'true';

      // Mobile toggle
      if (this.mobileToggle) {
        this.mobileToggle.addEventListener('click', () => this.openSidebar());
      }

      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.closeSidebar());
      }

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeSidebar());
      }

      // Filter group toggles
      this.filterToggles.forEach(toggle => {
        toggle.addEventListener('click', () => this.toggleFilterGroup(toggle));
      });

      // Filter changes - AJAX update
      if (this.filterForm) {
        const inputs = this.filterForm.querySelectorAll('input[type="checkbox"], input[type="number"]');
        inputs.forEach(input => {
          input.addEventListener('change', () => this.applyFilters());
          if (input.type === 'number') {
            input.addEventListener('input', () => this.updatePriceRanges());
          }
        });

        this.filterForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.applyFilters();
        });
      }
    }

    openSidebar() {
      this.sidebar.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-visible');
      document.body.classList.add('filter-drawer-open');
    }

    closeSidebar() {
      this.sidebar.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-visible');
      document.body.classList.remove('filter-drawer-open');
    }

    toggleFilterGroup(toggle) {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const content = toggle.nextElementSibling;

      toggle.setAttribute('aria-expanded', !isExpanded);

      if (content) {
        if (isExpanded) {
          content.style.maxHeight = '0';
          content.style.paddingBottom = '0';
        } else {
          content.style.maxHeight = content.scrollHeight + 'px';
          content.style.paddingBottom = '16px';
          this.updatePriceRanges();
        }
      }
    }

    initPriceRanges() {
      if (!this.sidebar) return;
      const ranges = this.sidebar.querySelectorAll('[data-price-range]');
      this.priceRanges = Array.from(ranges).map(rangeEl => this.setupPriceRange(rangeEl)).filter(Boolean);
      this.updatePriceRanges();
    }

    setupPriceRange(rangeEl) {
      const slider = rangeEl.querySelector('[data-price-slider]');
      const track = rangeEl.querySelector('[data-price-track]');
      const handleMin = rangeEl.querySelector('[data-handle-min]');
      const handleMax = rangeEl.querySelector('[data-handle-max]');
      const inputMin = rangeEl.querySelector('[data-price-min]');
      const inputMax = rangeEl.querySelector('[data-price-max]');

      if (!slider || !track || !handleMin || !handleMax || !inputMin || !inputMax) return null;

      const rangeMin = parseFloat(rangeEl.dataset.rangeMin ?? inputMin.min ?? 0);
      const rangeMax = parseFloat(rangeEl.dataset.rangeMax ?? inputMax.max ?? 0);
      const safeMin = Number.isFinite(rangeMin) ? rangeMin : 0;
      const safeMax = Number.isFinite(rangeMax) && rangeMax > safeMin ? rangeMax : safeMin + 1;

      const instance = {
        rangeEl,
        slider,
        track,
        handleMin,
        handleMax,
        inputMin,
        inputMax,
        min: safeMin,
        max: safeMax
      };

      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
      const toPercent = (value) => ((value - instance.min) / (instance.max - instance.min)) * 100;
      const readInput = (input, fallback) => {
        const parsed = parseFloat(input.value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const updateHandles = (activeHandle = null) => {
        let minValue = readInput(inputMin, instance.min);
        let maxValue = readInput(inputMax, instance.max);

        minValue = clamp(minValue, instance.min, instance.max);
        maxValue = clamp(maxValue, instance.min, instance.max);

        if (minValue > maxValue) {
          if (activeHandle === 'min') {
            minValue = maxValue;
          } else if (activeHandle === 'max') {
            maxValue = minValue;
          } else {
            minValue = maxValue;
          }
        }

        inputMin.value = Math.round(minValue);
        inputMax.value = Math.round(maxValue);

        const minPercent = toPercent(minValue);
        const maxPercent = toPercent(maxValue);

        handleMin.style.left = `${minPercent}%`;
        handleMax.style.left = `${maxPercent}%`;
        track.style.left = `${minPercent}%`;
        track.style.width = `${Math.max(0, maxPercent - minPercent)}%`;
      };

      const valueFromPointer = (clientX) => {
        const rect = slider.getBoundingClientRect();
        const percent = clamp((clientX - rect.left) / rect.width, 0, 1);
        return instance.min + percent * (instance.max - instance.min);
      };

      const startDrag = (activeHandle) => (event) => {
        event.preventDefault();
        const move = (moveEvent) => {
          const value = valueFromPointer(moveEvent.clientX);
          if (activeHandle === 'min') {
            inputMin.value = Math.round(value);
          } else {
            inputMax.value = Math.round(value);
          }
          updateHandles(activeHandle);
        };

        const stop = () => {
          document.removeEventListener('pointermove', move);
          document.removeEventListener('pointerup', stop);
          const targetInput = activeHandle === 'min' ? inputMin : inputMax;
          targetInput.dispatchEvent(new Event('change', { bubbles: true }));
        };

        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', stop, { once: true });
      };

      handleMin.addEventListener('pointerdown', startDrag('min'));
      handleMax.addEventListener('pointerdown', startDrag('max'));

      slider.addEventListener('click', (event) => {
        const value = valueFromPointer(event.clientX);
        const minValue = readInput(inputMin, instance.min);
        const maxValue = readInput(inputMax, instance.max);
        const closerToMin = Math.abs(value - minValue) <= Math.abs(value - maxValue);
        if (closerToMin) {
          inputMin.value = Math.round(value);
          updateHandles('min');
          inputMin.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          inputMax.value = Math.round(value);
          updateHandles('max');
          inputMax.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      inputMin.addEventListener('input', () => updateHandles('min'));
      inputMax.addEventListener('input', () => updateHandles('max'));

      instance.update = updateHandles;
      updateHandles();
      return instance;
    }

    updatePriceRanges() {
      if (!this.priceRanges || this.priceRanges.length === 0) return;
      this.priceRanges.forEach(range => range.update());
    }

    rebind() {
      this.refreshElements();
      if (!this.sidebar) return;
      this.bindEvents();
      this.initPriceRanges();
    }

    applyFilters() {
      const formData = new FormData(this.filterForm);
      const params = new URLSearchParams();

      for (const [key, value] of formData.entries()) {
        if (value) params.append(key, value);
      }

      const url = `${window.location.pathname}?${params.toString()}`;

      // Show loading state
      const grid = document.querySelector('[data-product-grid]');
      if (grid) grid.style.opacity = '0.5';

      fetch(url)
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Update product grid
          const newGrid = doc.querySelector('[data-product-grid]');
          const currentGrid = document.querySelector('[data-product-grid]');
          if (newGrid && currentGrid) {
            currentGrid.innerHTML = newGrid.innerHTML;
            currentGrid.style.opacity = '1';
          }

          // Update pagination
          const newPagination = doc.querySelector('.pagination');
          const currentPagination = document.querySelector('.pagination');
          if (currentPagination) {
            if (newPagination) {
              currentPagination.innerHTML = newPagination.innerHTML;
            } else {
              currentPagination.remove();
            }
          }

          // Update filters sidebar and mobile toggle
          const newSidebar = doc.querySelector('[data-collection-sidebar]');
          const currentSidebar = document.querySelector('[data-collection-sidebar]');
          if (newSidebar && currentSidebar) {
            currentSidebar.outerHTML = newSidebar.outerHTML;
          }

          const newToggle = doc.querySelector('[data-filter-mobile-toggle]');
          const currentToggle = document.querySelector('[data-filter-mobile-toggle]');
          if (newToggle && currentToggle) {
            currentToggle.outerHTML = newToggle.outerHTML;
          }

          const newOverlay = doc.querySelector('[data-filter-overlay]');
          const currentOverlay = document.querySelector('[data-filter-overlay]');
          if (newOverlay && currentOverlay) {
            currentOverlay.outerHTML = newOverlay.outerHTML;
          }

          // Update URL
          window.history.pushState({}, '', url);

          // Close mobile sidebar
          document.body.classList.remove('filter-drawer-open');

          // Rebind filters after replacing markup
          this.rebind();
        })
        .catch(error => {
          console.error('Filter error:', error);
          if (grid) grid.style.opacity = '1';
        });
    }
  }


  /* ========================================
     PREMIUM SORT DROPDOWN
     ======================================== */

  class PremiumSort {
    constructor() {
      this.dropdown = document.querySelector('[data-sort-dropdown]');
      if (!this.dropdown) return;

      this.trigger = this.dropdown.querySelector('[data-sort-trigger]');
      this.menu = this.dropdown.querySelector('[data-sort-menu]');
      this.options = this.dropdown.querySelectorAll('[data-sort-value]');
      this.currentLabel = this.dropdown.querySelector('[data-sort-current]');

      this.init();
    }

    init() {
      // Toggle dropdown
      this.trigger.addEventListener('click', () => this.toggle());

      // Option selection
      this.options.forEach(option => {
        option.addEventListener('click', () => this.selectOption(option));
      });

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!this.dropdown.contains(e.target)) {
          this.close();
        }
      });

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    toggle() {
      const isOpen = this.dropdown.classList.contains('is-open');
      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.dropdown.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.dropdown.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    selectOption(option) {
      const value = option.dataset.sortValue;
      const label = option.textContent.trim();

      // Update current label
      if (this.currentLabel) {
        this.currentLabel.textContent = label;
      }

      // Update active state
      this.options.forEach(opt => opt.classList.remove('is-active'));
      option.classList.add('is-active');

      // Close dropdown
      this.close();

      // Apply sort
      this.applySort(value);
    }

    applySort(value) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);

      // Show loading state
      const grid = document.querySelector('[data-product-grid]');
      if (grid) grid.style.opacity = '0.5';

      fetch(url)
        .then(response => response.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Update product grid
          const newGrid = doc.querySelector('[data-product-grid]');
          const currentGrid = document.querySelector('[data-product-grid]');
          if (newGrid && currentGrid) {
            currentGrid.innerHTML = newGrid.innerHTML;
            currentGrid.style.opacity = '1';
          }

          // Update URL
          window.history.pushState({}, '', url);
        })
        .catch(error => {
          console.error('Sort error:', error);
          if (grid) grid.style.opacity = '1';
        });
    }
  }


  /* ========================================
     QUICK ADD TO CART (Product Cards)
     ======================================== */

  class QuickAdd {
    constructor() {
      this.buttons = document.querySelectorAll('[data-quick-add]');
      this.init();
    }

    init() {
      this.buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.addToCart(btn);
        });
      });
    }

    addToCart(btn) {
      const variantId = btn.dataset.variantId;
      if (!variantId) {
        // Redirect to product page for variant selection
        const handle = btn.dataset.productHandle;
        if (handle) window.location.href = `/products/${handle}`;
        return;
      }

      btn.classList.add('is-loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1
        })
      })
      .then(response => response.json())
      .then(data => {
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');

        // Update button text
        const originalText = btn.innerHTML;
        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Added!';

        // Trigger cart update
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

        // Reset button
        setTimeout(() => {
          btn.classList.remove('is-success');
          btn.innerHTML = originalText;
        }, 2000);
      })
      .catch(error => {
        console.error('Quick add error:', error);
        btn.classList.remove('is-loading');
      });
    }
  }


  /* ========================================
     INITIALIZATION
     ======================================== */

  function initPremiumComponents() {
    // Product Gallery
    const galleries = document.querySelectorAll('[data-product-gallery]');
    galleries.forEach(gallery => new PremiumGallery(gallery));

    // Sticky Add to Cart
    new StickyAddToCart();

    // Variant Guide
    const variantGuides = document.querySelectorAll('[data-variant-guide]');
    variantGuides.forEach(guide => new VariantGuide(guide));

    // Collection Filters
    new PremiumFilters();

    // Sort Dropdown
    new PremiumSort();

    // Quick Add
    new QuickAdd();
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPremiumComponents);
  } else {
    initPremiumComponents();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', initPremiumComponents);

})();
